import {
  Affix,
  Alert,
  Box,
  Button,
  Container,
  Group,
  LoadingOverlay,
  Pagination,
  Paper,
  Stack,
  Text,
  Title,
  useMantineTheme
} from '@mantine/core'
import { useDebouncedValue, useMediaQuery, useWindowScroll } from '@mantine/hooks'
import { IconAlertCircle } from '@tabler/icons'
import Image from 'next/image'
import { usePathname, useSearchParams } from 'next/navigation'
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'

import { Features } from '@/components/commons'
import { useFetch } from '@/lib/hooks'
import { getSession, getUrlString } from '@/lib/utils'
import { useAuth } from '@/providers/AuthProvider'
import {
  Channel as ChannelType,
  Collection as CollectionType,
  Feature as FeatureType,
  Showcase as ShowcaseType,
  Site as SiteType,
  Store as StoreType
} from '@/types'

import { CollectionsDisplay, SinglePageDisplay } from './components'
import SearchField from './components/SearchField'

interface Data {
  site?: SiteType
  channel?: ChannelType
  store?: StoreType
  showcase?: ShowcaseType
}

interface Props {
  initialData?: Data
}

export default function ShowcaseView({ initialData }: Props) {
  // Hooks
  const { isAuthenticated } = useAuth() || {}
  const router = useRouter()
  const searchParams = useSearchParams()
  const pathname = usePathname()
  const [scroll, scrollTo] = useWindowScroll()
  const theme = useMantineTheme()
  const isXs = useMediaQuery(`(max-width: ${theme.breakpoints.xs}px)`)

  // Constants
  const {
    site: siteSlug,
    channel: channelSlug,
    store: storeSlug,
    showcase: showcaseSlug,
    collection,
    search
  } = router.query
  const collectionSlug: string = getUrlString(collection)
  const searchParam: string = getUrlString(search)

  // States
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [currentPageSize] = useState<number>(20)
  const [querySearch, setQuerySearch] = useState<string>(searchParam)
  const [debouncedSearch] = useDebouncedValue(querySearch, 1000)

  // Fetch
  const { data: siteData } = useFetch([siteSlug ? `/${siteSlug}/` : null], {
    fallbackData: initialData?.site,
    revalidateOnFocus: false
  })
  const sessionStudentUid = getSession('stuid')
  const isSitePrivate = siteData?.privacy === 'private'

  const { data: storeData } = useFetch(
    [
      siteSlug && channelSlug && storeSlug
        ? `/${siteSlug}/channels/${channelSlug}/stores/${storeSlug}/`
        : null
    ],
    { fallbackData: initialData?.store, revalidateOnFocus: false }
  )

  const {
    data: showcaseData,
    error: showcaseError,
    isValidating: showcaseIsValidating
  } = useFetch(
    [
      siteSlug && channelSlug && storeSlug && showcaseSlug
        ? `/${siteSlug}/channels/${channelSlug}/stores/${storeSlug}/showcases/${showcaseSlug}/`
        : null
    ],
    { fallbackData: initialData?.showcase, revalidateOnFocus: false }
  )
  const shouldFetchProducts =
    !!router.isReady &&
    !!siteData &&
    !!showcaseData &&
    !!(
      showcaseData.navigation === 'single-page' ||
      (showcaseData.navigation !== 'single-page' && collectionSlug)
    ) &&
    !!siteSlug &&
    !!channelSlug &&
    !!storeSlug &&
    !!showcaseSlug

  const {
    data: productsData,
    error: productsError,
    isLoading: productsIsLoading,
    mutate: mutateProducts
  } = useFetch(
    [
      shouldFetchProducts
        ? `/${siteSlug}/channels/${channelSlug}/stores/${storeSlug}/showcases/${showcaseSlug}/products/`
        : null,
      {
        search: debouncedSearch || '',
        page: currentPage,
        page_size: currentPageSize,
        ...(!debouncedSearch && collectionSlug && showcaseData?.navigation !== 'single-page'
          ? { collection: collectionSlug }
          : {}),
        ...(isSitePrivate ? { student_site_profile: sessionStudentUid || '' } : {})
      }
    ],
    { revalidateOnFocus: false }
  )

  // Constants
  const totalPages = productsData ? Math.ceil(productsData.count / currentPageSize) : 0
  const isValidCollectionSlug =
    Array.isArray(showcaseData?.collections) && collectionSlug
      ? showcaseData.collections
        .map((collection: CollectionType) => collection?.slug)
        .includes(collectionSlug)
      : true
  const firstCollectionSlug =
    Array.isArray(showcaseData?.collections) && showcaseData.collections[0]?.slug

  const showErrorAlert =
    (!showcaseIsValidating && !!showcaseError) ||
    (!productsIsLoading && !!productsError) ||
    (!productsIsLoading && showcaseData?.navigation !== 'single-page' && !isValidCollectionSlug)

  // Effects
  useEffect(() => {
    const params = new URLSearchParams(searchParams.toString())
    if (querySearch) params.set('search', querySearch)
    history.pushState(null, '', `${pathname}?${params.toString()}`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch])

  useEffect(() => {
    if (searchParam) setQuerySearch(searchParam)
  }, [searchParam])

  useEffect(() => setCurrentPage(1), [collectionSlug, debouncedSearch])

  useEffect(() => {
    if (
      !!router.isReady &&
      !!showcaseData &&
      showcaseData?.navigation !== 'single-page' &&
      !collectionSlug &&
      !!firstCollectionSlug
    ) {
      router.replace(
        `/${channelSlug}/${storeSlug}/${showcaseSlug}?collection=${firstCollectionSlug}`
      )
    }

    if (Array.isArray(showcaseData?.collections)) {
      showcaseData.collections.forEach((collection: CollectionType) =>
        router.prefetch(
          `/${channelSlug}/${storeSlug}/${showcaseSlug}?collection=${collection.slug}`
        )
      )
    }
  }, [
    router,
    showcaseData,
    collectionSlug,
    channelSlug,
    storeSlug,
    showcaseSlug,
    firstCollectionSlug
  ])

  useEffect(() => {
    mutateProducts()
  }, [isAuthenticated, mutateProducts])

  return (
    <Container size="xl" py="xl">
      <LoadingOverlay visible={productsIsLoading} />

      <Stack>
        {showcaseData?.featured
          ?.filter?.((feature: FeatureType) => feature.component === 'popup')
          .map?.((feature: FeatureType) => (
            <Features.Popup
              key={feature.uid}
              feature={feature}
              allFeatures={showcaseData?.featured?.filter(
                (feature: FeatureType) => feature.component === 'popup'
              )}
            />
          ))}
        {showcaseData?.featured
          ?.filter?.((feature: FeatureType) => feature.component === 'banner')
          .map?.((feature: FeatureType) => <Features.Banner key={feature.uid} feature={feature} />)}

        {showErrorAlert && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Erro de configuração"
            color="orange"
            hidden={productsIsLoading || (!!showcaseData?.collections?.length && !collectionSlug)}>
            <Text>
              {firstCollectionSlug
                ? 'Esta coleção que você procura não existe'
                : 'Houve um erro de configuração. Entre em contato na Central de Ajuda.'}
            </Text>
          </Alert>
        )}
        {showcaseData?.isEmpty && showcaseData?.displayEmptyMessage && (
          <>
            {siteData?.emptyShowcaseMessage ? (
              <Box dangerouslySetInnerHTML={{ __html: siteData?.emptyShowcaseMessage }}></Box>
            ) : (
              <Paper withBorder p={40}>
                <Stack display="flex" align="center" m="auto" maw={500} w="100%">
                  <Image
                    alt="Pedidos"
                    src="/images/placeholders/empty-order.png"
                    width={isXs ? '211' : '386'}
                    height={isXs ? '200' : '366'}
                  />
                  <Title align="center" order={2}>
                    Vitrine sem produtos
                  </Title>
                  <Text align="center" size="lg">
                    Nenhum produto está disponível para venda nesta vitrine
                  </Text>
                </Stack>
              </Paper>
            )}
          </>
        )}

        {showcaseData?.searchEnabled && (
          <SearchField search={querySearch} setSearch={setQuerySearch} />
        )}
        {!!querySearch && !!productsData?.count && (
          <Text>
            Pesquisando produtos pelo termo <strong>&quot;{querySearch}&quot;</strong>
          </Text>
        )}
        {!productsIsLoading && !!querySearch && !productsData?.count && (
          <Alert icon={<IconAlertCircle size={16} />} color="orange" title="Busca não encontrada">
            <Stack>
              <Text>
                Nenhum produto encontrado com o termo <strong>&quot;{querySearch}&quot;</strong>
              </Text>
              <Box display="flex">
                <Button color="orange" onClick={() => setQuerySearch('')}>
                  Limpar busca
                </Button>
              </Box>
            </Stack>
          </Alert>
        )}

        {(showcaseData?.navigation === 'collections' ||
          showcaseData?.navigation === 'single-page-collections') && (
          <CollectionsDisplay
            site={siteData}
            store={storeData}
            showcase={showcaseData}
            collections={showcaseData?.collections || []}
            products={productsData?.results || []}
            search={debouncedSearch}
          />
        )}
        {showcaseData?.navigation === 'single-page' && (
          <SinglePageDisplay
            site={siteData}
            store={storeData}
            showcase={showcaseData}
            products={productsData?.results || []}
          />
        )}
        {/* //Voltar regra depois que ajustar single-page-collections
        {showcaseData?.navigation === 'single-page-collections' && (
          <SinglePageCollectionsDisplay
            site={siteData}
            store={storeData}
            showcase={showcaseData}
            collections={showcaseData?.collections || []}
            products={productsData?.results || []}
          />
        )} */}

        <Group position="right" p="md" hidden={totalPages <= 1}>
          <Pagination
            total={totalPages}
            siblings={2}
            page={currentPage}
            onChange={setCurrentPage}
          />
        </Group>
      </Stack>

      <Affix hidden={scroll.y < 250} position={{ bottom: 10, right: 10 }}>
        <Button onClick={() => scrollTo({ y: 0 })}>Ir para o topo</Button>
      </Affix>
    </Container>
  )
}
